import { useEffect, useState, useContext, useRef } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, Link } from "react-router-dom";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import "./Login.css";
import { Account } from "./Main";
import { fetchWithAuth, fetchWithoutAuth, debug_mode } from "./Utils";


const Login = () => {
  document.title = "Sporting - Login/Registrati";

  const { setpersona, showAlert } = useContext(Account);
  const navigate = useNavigate();

  // ---------- Stati principali ----------
  const [mode, setMode] = useState("login"); // "login" | "signup" | "recover"
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  // Campi form
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [nascita, setNascita] = useState("");
  const [email, setEmail] = useState("");
  const [socio, setSocio] = useState(false);
  const [ricordami, setRicordami] = useState(false);

  // Password show/hide
  const [showPsw, setShowPsw] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !loading) {
        if (mode === "login") handleLogin();
        else if (mode === "signup") handleSignup();
        else if (mode === "recover") handleRecover();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mode, loading, username, password, email]);

  useEffect(() => {
    autoGenerateUsername();
  }, [nome, cognome]);

  const onVerifyCaptcha = (token) => {
    setCaptchaToken(token);
  };

  // ---------- Gestione Login ----------
  const handleLogin = async () => {
    if (!username || !password) {
      showAlert("Attenzione", "Inserisci username e password");
      return;
    }
    setLoading(true);

    try {
      const data = await fetchWithoutAuth("login.php", {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: JSON.stringify({ username, password }),
      });

      if (data.ok) {
        localStorage.clear();

        // Salva access token
        localStorage.setItem("access_token", data.access_token);

        // Salva refresh token se Ricordami
        if (ricordami) localStorage.setItem("refresh_token", data.refresh_token);

        setpersona({ ...data.user, access_token: data.access_token });

        // Verifica Sfide In Attesa
        try {
          const res = await fetchWithAuth("classifica_mobile_get_sfide.php", {}, "GET");

          if (res.ok) {
            setpersona(prev => ({
              ...prev,
              sfide_presenti: res.nuove
            }));
          }
        } catch (e) {
          if (debug_mode) console.error("Errore verifica sfide:", e);
        }

        navigate("/");
      } else {
        showAlert("Attenzione", data.error || "Credenziali non valide");
      }
    } catch (error) {
      console.error("Login error:", error);
      showAlert("Attenzione", error.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Gestione Registrazione ----------
  const handleSignup = async () => {
    if (!captchaToken && !debug_mode) {
      showAlert("Attenzione", "Verifica CAPTCHA");
      return;
    }

    if (!nome || !cognome || !nascita || !email || !username || !password) {
      showAlert("Attenzione", "Compila tutti i campi");
      return;
    }

    setLoading(true);
    try {
      const data = await fetchWithoutAuth("registra_utente.php", {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: JSON.stringify({
          nome,
          cognome,
          nascita,
          email: email.toLowerCase(),
          username: username.toLowerCase(),
          password,
          socio: socio ? 1 : 0,
          hcaptcha_token: captchaToken
        }),
      });

      if (debug_mode) console.log("Signup response:", data);

      if (data.ok) {
        showAlert("Attenzione", "Registrazione completata! Effettua il login.");
        setMode("login");
        resetForm();
      } else {
        showAlert("Attenzione", data.error || "Errore durante la registrazione");
      }
    } catch (error) {
      console.error("Registration error:", error);
      showAlert("Attenzione", error.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Recupero Password ----------
  const handleRecover = async () => {
    if (!email || !username) {
      showAlert("Attenzione", "Inserisci username e email");
      return;
    }
    setLoading(true);

    try {
      const data = await fetchWithoutAuth("recupera_password.php", {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email }),
      });

      if (data.ok) {
        showAlert("Attenzione", "Email di recupero inviata!");
        setMode("login");
        resetForm();
      } else {
        showAlert("Attenzione", data.error || "Errore nel recupero password");
      }
    } catch (error) {
      console.error("Recover error:", error);
      showAlert("Attenzione", error.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Utility ----------
  const resetForm = () => {
    setUsername("");
    setPassword("");
    setNome("");
    setCognome("");
    setNascita("");
    setEmail("");
    setSocio(false);
    setRicordami(false);
    setCaptchaToken("");
  };

  const autoGenerateUsername = () => {
    if (nome.length > 1 && cognome.length > 1) {
      const nm = nome.toLowerCase().replace(/\s/g, "");
      const cgnm = cognome.toLowerCase().replace(/\s/g, "");
      setUsername(`${nm}.${cgnm}`);
    }
  };

  // ---------- JSX ----------
  return (
    <>
      <Helmet>
        <title>Sporting - Login/Registrati</title>
        <meta
          name="description"
          content="Accedi o registrati alla piattaforma web di prenotazione di Sporting Saluzzo."
        />
      </Helmet>
      <div id="login">
        {loading && <div className="loading-overlay">Caricamento...</div>}

        <div id="login-cont">
          <h1>Accedi o registrati</h1>

          {/* ---- FORM UNICO ---- */}
          <form
            style={{
              width: "80%",
              display: "flex",
              boxSizing: "border-box",
              flexDirection: "column",
              alignItems: "center",
              margin: 0,
              padding: 0,
              marginBottom: 10
            }}
          >
            {/* ---- Campi Signup ---- */}
            {mode === "signup" && (
              <>
                <div className="campo">
                  <span>Nome</span>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Mario"
                  />
                </div>

                <div className="campo">
                  <span>Cognome</span>
                  <input
                    type="text"
                    value={cognome}
                    onChange={(e) => setCognome(e.target.value)}
                    placeholder="Rossi"
                  />
                </div>

                <div className="campo">
                  <span>Data di nascita</span>
                  <input
                    type="date"
                    value={nascita}
                    onChange={(e) => setNascita(e.target.value)}
                  />
                </div>

                <div className="campo">
                  <span>Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    placeholder="esempio@esempio.com"
                  />
                </div>

                <div className="socio-cont">
                  <span>Socio</span>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={socio}
                      onChange={() => setSocio(!socio)}
                    />
                    <span className="slider round"></span>
                  </label>

                  <span id="socio-info" style={{ opacity: socio ? 1 : 0 }}>
                    Necessari certificato medico e 5€ per l'assicurazione
                  </span>
                </div>

                <HCaptcha
                  sitekey="a6a28482-5de4-4c24-a528-c468e1ee41d2"
                  onVerify={onVerifyCaptcha}
                  ref={captchaRef}
                />
              </>
            )}

            {/* ---- Campi Recover ---- */}
            {mode === "recover" && (
              <>
                <div className="campo">
                  <span>Username</span>
                  <input
                    type="text"
                    placeholder="mario.rossi"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                    readOnly={mode === "signup"}
                  />
                </div>

                <div className="campo">
                  <span>Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    placeholder="esempio@esempio.com"
                  />
                </div>
              </>
            )}

            {/* ---- Campi Login + Signup comuni ---- */}
            {(mode === "login" || mode === "signup") && (
              <>
                <div className="campo">
                  <span>Username</span>
                  <input
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                    readOnly={mode === "signup"}
                  />
                </div>

                <div className="campo psw">
                  <span>Password</span>
                  <input
                    type={showPsw ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <i
                    className={
                      showPsw ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"
                    }
                    onClick={() => setShowPsw(!showPsw)}
                  ></i>
                </div>

                {mode === "login" && (
                  <div className="ricordami-cont">
                    <span>Ricordami</span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={ricordami}
                        onChange={() => setRicordami(!ricordami)}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                )}
              </>
            )}
          </form>

          {/* ---- Bottoni ---- */}
          <div id="btn-login">
            {mode === "login" && (
              <>
                <input type="button" value="Entra" onClick={handleLogin} disabled={loading} />
                <input
                  type="button"
                  value="Registrati"
                  onClick={() => {
                    setMode("signup");
                    resetForm();
                  }}
                  disabled={loading}
                />
                <input
                  type="button"
                  value="Recupera password"
                  onClick={() => {
                    setMode("recover");
                    resetForm();
                  }}
                  disabled={loading}
                />
              </>
            )}

            {mode === "signup" && (
              <>
                <input
                  type="button"
                  value="Login"
                  onClick={() => {
                    setMode("login");
                    resetForm();
                  }}
                  disabled={loading}
                />
                <input
                  type="button"
                  value="Conferma"
                  onClick={handleSignup}
                  disabled={loading}
                />
              </>
            )}

            {mode === "recover" && (
              <>
                <input
                  type="button"
                  value="Torna al login"
                  onClick={() => {
                    setMode("login");
                    resetForm();
                  }}
                  disabled={loading}
                />
                <input
                  type="button"
                  value="Invia Email"
                  onClick={handleRecover}
                  disabled={loading}
                />
              </>
            )}
          </div>

          {mode === "signup" && (
            <h5
              style={{
                fontSize: 17,
                margin: 0,
                padding: 15,
                textAlign: "center",
                color: "var(--primary)"
              }}
            >
              Registrandoti accetti la nostra <Link to="/privacy">Privacy Policy</Link>
            </h5>
          )}
        </div>
      </div>
    </>
  );
};

export default Login;
